import React, { useState } from 'react';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import { PROFILE_DATA } from '../../data/config';
import { Mail, Phone, MapPin, Copy, Check, Send } from 'lucide-react';

const Contact = () => {
  const [copied, setCopied] = useState(false);
  const [formData, setFormData] = useState({ name: '', email: '', message: '' });
  const [submitted, setSubmitted] = useState(false);

  const copyToClipboard = () => {
    navigator.clipboard.writeText(PROFILE_DATA.email);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // In a real application, you would send this data to a backend
    console.log('Form submitted:', formData);
    setSubmitted(true);
    setTimeout(() => {
      setSubmitted(false);
      setFormData({ name: '', email: '', message: '' });
    }, 3000);
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <section id="contact" className="section-container">
      <div className="max-w-6xl mx-auto">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold mb-4 gradient-text">Get In Touch</h2>
          <div className="w-24 h-1 bg-gradient-to-r from-coffee-500 to-coffee-300 mx-auto mb-4"></div>
          <p className="text-coffee-200 text-lg">
            Let's discuss how we can work together
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12">
          {/* Contact Info & Map */}
          <div className="space-y-6">
            {/* Contact Cards */}
            <div className="glass-card p-6">
              <h3 className="text-xl font-semibold text-cream-100 mb-6">Contact Information</h3>
              
              <div className="space-y-4">
                {/* Email */}
                <div className="flex items-start gap-4 p-4 rounded-lg bg-coffee-700/20 hover:bg-coffee-700/30 transition-colors group">
                  <Mail className="w-5 h-5 text-coffee-300 flex-shrink-0 mt-1" />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-coffee-200 mb-1">Email</div>
                    <div className="text-cream-100 font-mono text-sm break-all">
                      {PROFILE_DATA.email}
                    </div>
                  </div>
                  <button
                    onClick={copyToClipboard}
                    className="p-2 rounded-lg bg-coffee-600/30 hover:bg-coffee-600/50 transition-colors"
                    title="Copy email"
                  >
                    {copied ? (
                      <Check className="w-4 h-4 text-green-500" />
                    ) : (
                      <Copy className="w-4 h-4 text-coffee-300" />
                    )}
                  </button>
                </div>

                {/* Phone */}
                <div className="flex items-start gap-4 p-4 rounded-lg bg-coffee-700/20">
                  <Phone className="w-5 h-5 text-coffee-300 flex-shrink-0 mt-1" />
                  <div>
                    <div className="text-sm text-coffee-200 mb-1">Phone</div>
                    <div className="text-cream-100 font-mono text-sm">
                      {PROFILE_DATA.phone}
                    </div>
                  </div>
                </div>

                {/* Location */}
                <div className="flex items-start gap-4 p-4 rounded-lg bg-coffee-700/20">
                  <MapPin className="w-5 h-5 text-coffee-300 flex-shrink-0 mt-1" />
                  <div>
                    <div className="text-sm text-coffee-200 mb-1">Location</div>
                    <div className="text-cream-100">
                      {PROFILE_DATA.location.city}, {PROFILE_DATA.location.country}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Map */}
            <div className="glass-card p-6">
              <h3 className="text-xl font-semibold text-cream-100 mb-4">Location</h3>
              <div className="rounded-xl overflow-hidden h-64 bg-coffee-700/30">
                <MapContainer
                  center={PROFILE_DATA.location.coords}
                  zoom={13}
                  scrollWheelZoom={false}
                  className="h-full w-full"
                >
                  <TileLayer
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  />
                  <Marker position={PROFILE_DATA.location.coords}>
                    <Popup>
                      {PROFILE_DATA.location.city}, {PROFILE_DATA.location.country}
                    </Popup>
                  </Marker>
                </MapContainer>
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <div className="glass-card p-8">
            <h3 className="text-xl font-semibold text-cream-100 mb-6">Send a Message</h3>
            
            {submitted ? (
              <div className="flex flex-col items-center justify-center py-12">
                <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mb-4">
                  <Check className="w-8 h-8 text-green-500" />
                </div>
                <h4 className="text-xl font-semibold text-cream-100 mb-2">Message Sent!</h4>
                <p className="text-coffee-200 text-center">
                  Thank you for reaching out. I'll get back to you soon.
                </p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Name */}
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-coffee-200 mb-2">
                    Name
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 bg-coffee-700/30 border border-coffee-600/30 rounded-lg text-cream-100 placeholder-coffee-300 focus:outline-none focus:border-coffee-500/50 focus:ring-2 focus:ring-coffee-500/20 transition-all"
                    placeholder="Your name"
                  />
                </div>

                {/* Email */}
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-coffee-200 mb-2">
                    Email
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 bg-coffee-700/30 border border-coffee-600/30 rounded-lg text-cream-100 placeholder-coffee-300 focus:outline-none focus:border-coffee-500/50 focus:ring-2 focus:ring-coffee-500/20 transition-all"
                    placeholder="your.email@example.com"
                  />
                </div>

                {/* Message */}
                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-coffee-200 mb-2">
                    Message
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows="6"
                    className="w-full px-4 py-3 bg-coffee-700/30 border border-coffee-600/30 rounded-lg text-cream-100 placeholder-coffee-300 focus:outline-none focus:border-coffee-500/50 focus:ring-2 focus:ring-coffee-500/20 transition-all resize-none"
                    placeholder="Tell me about your project..."
                  ></textarea>
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  className="w-full btn-primary flex items-center justify-center gap-2"
                >
                  <Send className="w-5 h-5" />
                  Send Message
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
